import React, { useEffect, useState } from 'react';
import { normalizeCommaDelimitedEmailText } from '../utils/emailRecipients';

const copyTextToClipboard = async (text) => {
  if (!text) return;
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const area = document.createElement('textarea');
  area.value = text;
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  document.body.removeChild(area);
};

const EmailRecipientsModal = ({
  open,
  title = 'Email Recipients',
  subtitle = 'Edit the comma-delimited list, then click Copy.',
  initialValue = '',
  onClose,
}) => {
  const [text, setText] = useState(initialValue || '');
  const [copied, setCopied] = useState(false);

  const uniqueCount = React.useMemo(() => {
    const emails = String(text || '')
      .split(',')
      .map((part) => String(part || '').trim().toLowerCase())
      .filter((part) => part && part.includes('@'));
    return new Set(emails).size;
  }, [text]);

  useEffect(() => {
    if (!open) return;
    setText(initialValue || '');
    setCopied(false);
  }, [open, initialValue]);

  if (!open) return null;

  const handleCopy = async () => {
    const normalized = normalizeCommaDelimitedEmailText(text);
    setText(normalized);
    if (!normalized) return;
    try {
      await copyTextToClipboard(normalized);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Ignore clipboard failures silently to match existing behavior.
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          padding: 18,
          borderRadius: 8,
          width: 'min(900px, 95%)',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 8px 28px rgba(0,0,0,0.3)',
        }}
      >
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <div style={{ marginBottom: 8, color: '#555' }}>{subtitle}</div>
        <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
          Total unique emails: {uniqueCount}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          style={{
            width: '100%',
            padding: 8,
            fontSize: 13,
            borderRadius: 6,
            border: '1px solid #ddd',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button type="button" className="cancel-btn" onClick={onClose}>Close</button>
          <button type="button" className="apply-btn" onClick={handleCopy}>{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </div>
    </div>
  );
};

export default EmailRecipientsModal;
